import { RefObject, useEffect, useState } from 'react'

export interface ChartTokens {
  accent: string
  /** The accent at low alpha, for area fills under a line. */
  accentFade: string
  success: string
  danger: string
  text3: string
  border: string
  fontFamily: string
}

const read = (style: CSSStyleDeclaration, name: string) => style.getPropertyValue(name).trim()

/** Normalises any CSS colour through canvas and returns it with the given alpha. */
export const withAlpha = (color: string, alpha: number) => {
  const ctx = document.createElement('canvas').getContext('2d')
  if (!ctx || !color) return color
  ctx.fillStyle = 'transparent'
  ctx.fillStyle = color
  const normalised = String(ctx.fillStyle)
  if (normalised.startsWith('#')) {
    const r = parseInt(normalised.slice(1, 3), 16)
    const g = parseInt(normalised.slice(3, 5), 16)
    const b = parseInt(normalised.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }
  const match = normalised.match(/rgba?\(([^)]+)\)/)
  if (!match) return color
  const [r, g, b, a = '1'] = match[1].split(',').map((v) => v.trim())
  return `rgba(${r}, ${g}, ${b}, ${parseFloat(a) * alpha})`
}

const readTokens = (el?: Element | null): ChartTokens => {
  const style = getComputedStyle(el ?? document.documentElement)
  const accent = read(style, '--plug-accent')
  return {
    accent,
    accentFade: withAlpha(accent, 0.2),
    success: read(style, '--plug-success'),
    danger: read(style, '--plug-danger'),
    text3: read(style, '--plug-text-3'),
    border: read(style, '--plug-border'),
    fontFamily: read(style, '--plug-font-text'),
  }
}

/**
 * Plug semantic colours as literal strings, for canvas chart libraries that cannot read CSS
 * variables. Re-reads when the tone (or theme) attribute on <html> changes. Pass the chart's
 * container so local overrides (e.g. inside a sheet) apply.
 */
export const useChartTokens = (ref?: RefObject<Element | null>): ChartTokens => {
  const [tokens, setTokens] = useState<ChartTokens>(() => readTokens(ref?.current))

  useEffect(() => {
    const update = () => setTokens(readTokens(ref?.current))
    update()
    const observer = new MutationObserver(update)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-tone', 'data-theme'],
    })
    return () => observer.disconnect()
  }, [ref])

  return tokens
}
